import React, { useEffect, useRef, useCallback } from 'react';
import moment from 'moment';
import { Button, Card, Progress, Statistic } from 'antd';
import { useNavigate } from "react-router-dom";
import { useFetchServiceableUrlsCountQuery } from '../serviceableUrls/service';
import { useFetchOnPageSeoScoreQuery, useFetchWebsiteIndexEntriesQuery } from '../websiteIndexEntry/service';
import { getProgressColorByScore } from '../utils';
import SmallSpinner from '../shared/SmallSpinner';
import RecheckAllUrlsButton from '../failedChecks/RecheckAllUrlsButton';
import DownloadPdfButton from './DownloadPdfButton';
import './Dashboard.less';

function getFormattedDate(date: string) {
    return moment.utc(date).local().format('MMM DD, YYYY [at] hh:mm A');
}

function formatNumber(val: number) {
    if (val >= 1_000_000) {
        val = val / 1_000_000;
        return val.toFixed(1) + ' M';
    }

    if (val >= 1_000) {
        val = val / 1_000;

        if (val < 10) {
            return val.toFixed(1) + ' K';
        }

        return val.toFixed(0) + ' K';
    }

    return val;
}

type Props = {
    websiteGuid: string;
    faviconUrl: string | null;
    homepageScreenShotUrl: string | null;
    summary: string;
};

export default function OverviewHeader({ websiteGuid, faviconUrl, homepageScreenShotUrl, summary }: Props) {
    const navigate = useNavigate();
    const summaryRef = useRef<HTMLDivElement | null>(null);

    const { data: websiteIndexEntries, isLoading: isLoadingWebsiteIndexEntries } = useFetchWebsiteIndexEntriesQuery();
    const { data: serviceableUrlsCount, isLoading: isLoadingServiceableUrlsCount } = useFetchServiceableUrlsCountQuery({ w_guid: websiteGuid });
    const { data: onPageSeoScore } = useFetchOnPageSeoScoreQuery({ guid: websiteGuid });
    const selectedWebsite = websiteIndexEntries?.find((website) => website.guid === websiteGuid);

    const handleOnPageGroupClick = useCallback((event: MouseEvent) => {
        const target = event.target as HTMLElement;
        const pageGroupId = target.getAttribute('data-page-group-id');
        if (pageGroupId) {
            navigate(`/deep-analysis/page-group/?w_guid=${websiteGuid}&cluster_guid=${pageGroupId}`);
        }
    }, [navigate, websiteGuid]);


    useEffect(() => {
        const summaryEl = summaryRef.current;
        if (!summaryEl) return;

        const spans = summaryEl.querySelectorAll<HTMLSpanElement>('.page-group-name');

        spans.forEach((span) => {
            span.addEventListener('click', handleOnPageGroupClick);
        });

        return () => {
            spans.forEach((span) => {
                span.removeEventListener('click', handleOnPageGroupClick);
            });
        };
    }, [summary, handleOnPageGroupClick]);

    return (
        <Card
            className="website-audit-header"
            loading={isLoadingWebsiteIndexEntries}
        >

            <div className="header-item homepage-screenshot">
                {/* <div className="stand">
                    <div className="monitor">
                        <img
                            width="305"
                            alt="screen-shot"
                            src={getScreenshotUrl(organizationGuid, websiteGuid)}
                        />
                    </div>
                </div> */}

                <div className="monitor">
                    {homepageScreenShotUrl ? <img width="300" alt="homepage-screenshot" src={homepageScreenShotUrl} /> : <div className="no-screenshot" style={{ width: "300px", height: "180px" }}></div>}
                </div>

            </div>

            <div className="header-item summary-container">
                <div className="summary-item domain">
                    <div className="favicon-and-title">
                        {faviconUrl ? <img className="favicon" src={faviconUrl} alt={`Favicon of ${selectedWebsite?.domain}`} /> : <img className="favicon" src="/images/default_favicon.png" alt="Default favicon" />}
                        <div className="title">
                            <div className="url">{selectedWebsite?.domain}</div>
                            {selectedWebsite?.on_page_seo && <span className="execution-date">Monitored weekly: Last checked on {getFormattedDate(selectedWebsite?.on_page_seo?.c_at)}</span>}
                        </div>
                    </div>
                    <div className="domain-actions">
                        <RecheckAllUrlsButton selectedWebsiteGuid={websiteGuid} />
                        <DownloadPdfButton websiteGuid={websiteGuid} />
                    </div>
                </div>

                <div
                    ref={summaryRef}
                    className="summary-item summary"
                    dangerouslySetInnerHTML={{ __html: summary }}
                ></div>

                <div className="summary-item extra">
                    {selectedWebsite && selectedWebsite.discovered_urls ? <div className="extra-item">
                        <Statistic
                            title="Discovered URLs"
                            prefix={
                                isLoadingServiceableUrlsCount ? <SmallSpinner /> : (
                                    <div className="overview-card-content">
                                        <span>{formatNumber(selectedWebsite.discovered_urls || 0)}</span>
                                    </div>
                                )}
                            formatter={() => null}
                        />
                    </div> : null}

                    <div className="extra-item">
                        <Statistic
                            title="Monitored URLs"
                            prefix={
                                isLoadingServiceableUrlsCount ? <SmallSpinner /> : (
                                    <div className="overview-card-content">
                                        <span>{serviceableUrlsCount?.count || 0}</span>
                                        <Button
                                            className="view-details"
                                            size="small"
                                            icon="eye"
                                            onClick={() => navigate(`/deep-analysis/monitored-urls?w_guid=${websiteGuid}`)}
                                        />
                                    </div>
                                )}
                            formatter={() => null}
                        />
                    </div>
                </div>
            </div>
            <div className="header-item seo-score-container">
                <div className="seo-score">
                    <Progress
                        type="circle"
                        percent={onPageSeoScore?.overall_score || 0}
                        strokeWidth={8}
                        strokeColor={getProgressColorByScore(onPageSeoScore?.overall_score || 0)}
                        format={(percent) => `${percent}/100`}
                    />
                    <span className="progress-label">Domain Score</span>
                </div>
            </div>
        </Card>
    );
}
